import { useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const RefundModal = ({ payment, onRefunded, onClose }) => {
  const remaining = +(payment.amount - (payment.refunded_amount || 0)).toFixed(2);
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const value = parseFloat(amount);
    if (!value || value <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/payments/${payment.id}/refund`,
        { amount: value },
        { withCredentials: true }
      );
      toast.success(
        data.status === "refunded"
          ? `Fully refunded $${data.refunded.toFixed(2)}`
          : `Refunded $${data.refunded.toFixed(2)} — $${data.remaining.toFixed(2)} left on the charge`
      );
      onRefunded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <form
        data-testid="refund-modal"
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-extrabold text-brand-navy">Refund {payment.customer_name}</h3>
            <p className="mt-1 text-xs font-semibold text-slate-500">
              Charge ${payment.amount.toFixed(2)}
              {payment.refunded_amount > 0 && ` · already refunded $${payment.refunded_amount.toFixed(2)}`}
              {` · up to $${remaining.toFixed(2)} left`}
            </p>
          </div>
          <button data-testid="refund-modal-close" type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <label className="mt-5 block text-xs font-bold uppercase tracking-widest text-slate-500">Refund amount (USD)</label>
        <div className="mt-2 flex items-center rounded-xl border border-slate-200 bg-slate-50 px-4 focus-within:border-brand-blue">
          <span className="font-display text-xl font-extrabold text-slate-400">$</span>
          <input
            data-testid="refund-amount-input"
            type="number"
            min="0.01"
            max={remaining}
            step="0.01"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-transparent px-2 py-3.5 font-display text-xl font-extrabold text-brand-navy focus:outline-none"
          />
        </div>
        <button
          data-testid="refund-full-button"
          type="button"
          onClick={() => setAmount(remaining.toFixed(2))}
          className="mt-2 text-xs font-bold text-brand-blue hover:underline"
        >
          Refund the full ${remaining.toFixed(2)}
        </button>
        <button
          data-testid="refund-submit-button"
          type="submit"
          disabled={busy}
          className="mt-4 w-full rounded-xl bg-rose-600 px-5 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-white transition-transform hover:scale-[1.02] disabled:opacity-50"
        >
          {busy ? "Refunding…" : "Issue refund"}
        </button>
      </form>
    </div>
  );
};
